import clsx from "clsx";
import { type ComponentProps, useMemo } from "react";

import type { TableBlockType } from "@features/article-content/model";

import { Body, Heading } from "@shared/ui/typography";

import s from "./table.module.scss";

export type TableProps = ComponentProps<"div"> & {
  data: TableBlockType;
};

export const Table = (props: TableBlockType) => {
  const { title, tabletRows, columnTitle = [], columnSubtitle } = props;

  const style: React.CSSProperties & { ["--columns"]: string } = {
    "--columns":
      columnTitle?.length === 0 ? "2" : (columnTitle?.length ?? 2).toString(),
  };

  const isSmall = useMemo(() => {
    return (columnTitle?.length ?? 0) < 3;
  }, [columnTitle]);

  return (
    <div className={s.root} style={style}>
      {title && (
        <Heading level={6} className={s.title}>
          {title}
        </Heading>
      )}
      <div className={s.subtitles}>
        {columnSubtitle &&
          columnSubtitle?.length > 0 &&
          columnSubtitle.map(
            (item, idx) =>
              item.title && (
                <Body
                  tag="div"
                  key={`subtitle-row_item-${item?.title}-${idx + 1}`}
                  weight="semiBold"
                >
                  {item.title}
                </Body>
              ),
          )}
      </div>
      <table className={s.container}>
        <thead>
          <tr className={s.row}>
            {columnTitle?.map(
              ({ title, id }) =>
                title && (
                  <Body tag="td" key={`${title}_${id}`} weight="semiBold">
                    {title}
                  </Body>
                ),
            )}
          </tr>
        </thead>
        <tbody className={s.body}>
          {tabletRows?.length > 0 &&
            tabletRows.map((row, idx) => (
              <tr
                key={`row_${idx + 1}`}
                className={clsx(s.row, { [s.witoutLabelMobile]: isSmall })}
              >
                {row?.rowItem?.map(
                  (item, index) =>
                    item && (
                      <Body
                        tag="td"
                        key={`row_${idx}_cell_${columnTitle?.[index]?.title ?? index}_${item?.text ?? index}`}
                        data-label={`${columnTitle?.[index]?.title ?? ""}`}
                        weight={index === 0 ? "semiBold" : "regular"}
                      >
                        {item?.text ?? ""}
                      </Body>
                    ),
                )}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

Table.displayName = "Table";
